import { resolveThemeAiImageStyle } from "common/utils/themeAiSettings";

/** Reads from either a loaded Theme model or the plain attributes object the theme editor holds. */
export function readThemeAttribute(theme, key) {
    if (!theme) {
        return undefined;
    }

    const value = typeof theme.get === "function" ? theme.get(key) : theme[key];
    return value ?? undefined;
}

/** The AI settings a kit carries, empty when it has none. */
export function getThemeAiRules(theme) {
    return readThemeAttribute(theme, "aiRules") ?? {};
}

/** The brand voice a kit defines, for surfaces that rewrite existing copy. */
export function getThemeBrandVoice(theme) {
    return getThemeAiRules(theme).voiceAndTone ?? undefined;
}

/**
 * The AI generation defaults a brand kit contributes. Defaults, not constraints — any of them can
 * still be overridden for a single generation.
 *
 * Fields the kit doesn't set are left undefined so callers fall through to their own fallbacks —
 * for the image sources that means resolveImageSource(), which knows what the plan allows.
 */
export function getThemeAiDefaults(theme) {
    const { imageSource, fallbackImageSource, language } = getThemeAiRules(theme);

    return {
        imageSource: imageSource ?? undefined,
        fallbackImageSource: fallbackImageSource ?? undefined,
        imageStyle: resolveThemeAiImageStyle({
            imageStyle: readThemeAttribute(theme, "imageStyle"),
            imageStylePrompt: readThemeAttribute(theme, "imageStylePrompt")
        }),
        language: language ?? undefined
    };
}

/**
 * Layers a brand kit's AI defaults under the values already in hand. An image choice is taken from
 * the kit unless the caller says not to, which it should only say for a value the user picked by
 * hand. Source and style are asked separately because they are overridden separately.
 */
export function mergeThemeAiDefaults(current, themeDefaults, {
    adoptImageDefaults = true,
    adoptImageSource = adoptImageDefaults,
    adoptFallbackImageSource = adoptImageSource,
    adoptImageStyle = adoptImageDefaults
} = {}) {
    const imageSource = adoptImageSource
        ? themeDefaults.imageSource ?? current.imageSource
        : current.imageSource ?? themeDefaults.imageSource;
    // The fallback only exists to back the source up, so it follows the source unless the caller
    // has a finer answer for it.
    const fallbackImageSource = adoptFallbackImageSource
        ? themeDefaults.fallbackImageSource ?? current.fallbackImageSource
        : current.fallbackImageSource ?? themeDefaults.fallbackImageSource;
    const imageStyle = adoptImageStyle
        ? themeDefaults.imageStyle ?? current.imageStyle
        : current.imageStyle ?? themeDefaults.imageStyle;

    return {
        imageSource,
        fallbackImageSource,
        imageStyle,
        language: themeDefaults.language ?? current.language
    };
}
